import React, { useState } from "react";
import {
  CloudUpload,
  Storage,
  Business,
  Folder,
  Work,
  Archive,
  ArrowForward,
  ArrowBack,
  Check,
  FolderOpen,
  Description,
  Search,
  CheckCircle
} from "@mui/icons-material";
import {
  Button,
  TextField,
  Checkbox,
  CircularProgress,
  LinearProgress,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Breadcrumbs,
  Link
} from "@mui/material";
import { CLOUD_SOURCES, CLOUD_CONFIG } from "../../data/config";
import { CloudUtils } from "../../data/utils";
import {
  CloudSource,
  CloudItem,
  CloudFile,
  CrawlerStep
} from "../../data/models";
import "./CloudCrawler.scss";
import { color } from "framer-motion";

interface CloudCrawlerProps {
  onComplete: (files: File[], classificationName: string) => void;
  onCancel: () => void;
}

const CloudCrawler: React.FC<CloudCrawlerProps> = ({
  onComplete,
  onCancel
}) => {
  const [step, setStep] = useState<CrawlerStep>(CrawlerStep.SOURCE);
  const [selectedSource, setSelectedSource] = useState<CloudSource | null>(
    null
  );
  const [importingProgress, setImportingProgress] = useState(0);
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [currentItems, setCurrentItems] = useState<CloudItem[]>([]);
  const [classificationName, setClassificationName] = useState<string>("");

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case "CloudUpload":
        return CloudUpload;
      case "Storage":
        return Storage;
      case "Business":
        return Business;
      case "Folder":
        return Folder;
      case "Work":
        return Work;
      case "Archive":
        return Archive;
      default:
        return CloudUpload;
    }
  };

  const handleSourceSelect = (source: CloudSource) => {
    setSelectedSource(source);
    setStep(CrawlerStep.CONNECTING);

    // Simulate connection process
    setTimeout(() => {
      setCurrentItems(CloudUtils.getMockCloudItems([]));
      setStep(CrawlerStep.BROWSE);
    }, 1500);
  };

  const handleFolderClick = (folder: any) => {
    const newPath = [...currentPath, folder.name];
    setCurrentPath(newPath);
    setCurrentItems(CloudUtils.getMockCloudItems(newPath));
  };

  const handleBackClick = () => {
    if (currentPath.length > 0) {
      const newPath = currentPath.slice(0, -1);
      setCurrentPath(newPath);
      setCurrentItems(CloudUtils.getMockCloudItems(newPath));
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      // Home clicked
      setCurrentPath([]);
      setCurrentItems(CloudUtils.getMockCloudItems([]));
    } else {
      const newPath = currentPath.slice(0, index + 1);
      setCurrentPath(newPath);
      setCurrentItems(CloudUtils.getMockCloudItems(newPath));
    }
  };

  const handleToggleItem = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === currentItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(currentItems.map((item) => item.id)));
    }
  };

  const handleStartImport = () => {
    const selectedFiles = currentItems
      .filter((item) => selectedItems.has(item.id) && item.type === "file")
      .map((item) => CloudUtils.createFileFromCloudItem(item as CloudFile));

    if (selectedFiles.length === 0 || !classificationName.trim()) {
      return;
    }

    onComplete(selectedFiles, classificationName.trim());
  };

  const filteredItems = currentItems.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedCount = selectedItems.size;
  const selectedFiles = currentItems.filter(
    (item) => selectedItems.has(item.id) && item.type === "file"
  );
  const totalFilesSelected = selectedFiles.length;

  return (
    <div className="cloud-crawler">
      {step === CrawlerStep.SOURCE && (
        <Card className="source-selection">
          <CardContent className="source-content">
            <Box className="header-section">
              <Typography variant="h5" className="header-title">
                Connect Cloud Storage
              </Typography>
              <Typography variant="body1" className="header-subtitle">
                Choose your cloud storage provider to browse and import
                documents
              </Typography>
            </Box>

            <Box className="sources-grid">
              {CLOUD_SOURCES.map((source) => {
                const IconComponent = getIconComponent(source.icon);
                return (
                  <Card
                    key={source.id}
                    className="source-card"
                    onClick={() => handleSourceSelect(source)}
                    sx={{ cursor: "pointer" }}
                  >
                    <CardContent className="source-card-content">
                      <Box
                        className="source-icon-container"
                        sx={{ color: source.color }}
                      >
                        <IconComponent className="source-icon" />
                      </Box>
                      <Box className="source-details">
                        <div className="source-name">{source.name}</div>
                        <div className="source-description">
                          {source.description}
                        </div>
                      </Box>
                      <ArrowForward className="arrow-icon" />
                    </CardContent>
                  </Card>
                );
              })}
            </Box>

            <Box className="footer-actions">
              <Button onClick={onCancel} variant="outlined">
                Cancel
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {step === CrawlerStep.CONNECTING && selectedSource && (
        <Card className="connecting-view">
          <CardContent className="connecting-content">
            <Box className="connecting-icon-container">
              <Box
                className="source-icon-wrapper"
                sx={{ color: selectedSource.color }}
              >
                {React.createElement(getIconComponent(selectedSource.icon), {
                  className: "source-icon"
                })}
              </Box>
              <CircularProgress className="loading-spinner" />
            </Box>
            <Typography variant="h5" className="connecting-title">
              Connecting to {selectedSource.name}
            </Typography>
            <Typography variant="body1" className="connecting-subtitle">
              Authenticating and loading your files...
            </Typography>
          </CardContent>
        </Card>
      )}

      {step === CrawlerStep.BROWSE && selectedSource && (
        <Card className="browse-view">
          {/* Header */}
          <Box className="browse-header">
            <Box className="header-info">
              <Box className="source-info">
                {React.createElement(getIconComponent(selectedSource.icon), {
                  className: "header-source-icon",
                  style: { color: selectedSource.color }
                })}
                <Box>
                  <div
                    className="source-name"
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: 600,
                      color: "#000000",
                      margin: "0 0 4px 0",
                      fontFamily: '"EY Interstate", Arial, sans-serif'
                    }}
                  >
                    {selectedSource.name}
                  </div>
                  {/* <Breadcrumbs className="breadcrumb-nav">
                    <Link
                      component="button"
                      onClick={() => handleBreadcrumbClick(-1)}
                      className="breadcrumb-link"
                    >
                      Home
                    </Link>
                    {currentPath.map((folder, index) => (
                      <Link
                        key={index}
                        component="button"
                        onClick={() => handleBreadcrumbClick(index)}
                        className="breadcrumb-link"
                      >
                        {folder}
                      </Link>
                    ))}
                  </Breadcrumbs> */}
                </Box>
              </Box>
              <Button
                onClick={() => {
                  setStep(CrawlerStep.SOURCE);
                  setSelectedSource(null);
                }}
                variant="outlined"
                size="small"
              >
                Change Source
              </Button>
            </Box>

            <TextField
              placeholder="Search files and folders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              size="small"
              className="search-field"
              InputProps={{
                startAdornment: <Search className="search-icon" />
              }}
            />
          </Box>

          {/* Navigation Bar */}
          <Box className="navigation-bar">
            <Box className="nav-left">
              {currentPath.length > 0 && (
                <Button
                  onClick={handleBackClick}
                  variant="text"
                  size="small"
                  startIcon={<ArrowBack />}
                >
                  Back
                </Button>
              )}
              <Button
                onClick={handleSelectAll}
                variant="text"
                size="small"
                className="select-all-button"
              >
                {selectedItems.size === currentItems.length
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </Box>
            <Typography variant="body2" className="items-count">
              {filteredItems.length} items
            </Typography>
          </Box>

          <Divider />

          {/* Classification Name Input - Show when files are selected */}
          {selectedFiles.length > 0 && (
            <Box className="classification-name-section">
              <Typography variant="body2" className="name-label">
                Classification Name <span className="required-asterisk">*</span>
              </Typography>
              <TextField
                placeholder="Enter a name for this batch"
                value={classificationName}
                onChange={(e) => setClassificationName(e.target.value)}
                variant="outlined"
                size="small"
                className="name-input"
                fullWidth
                required
              />
              {/* {!classificationName.trim() && ( */}
              <Typography variant="caption" className="name-hint">
                Please provide a name for classification of your documents
              </Typography>
              {/* )} */}
            </Box>
          )}

          {/* File/Folder List */}
          <Box className="items-list">
            {filteredItems.length === 0 ? (
              <Box className="empty-state">
                <Typography variant="body1" color="textSecondary">
                  No items found
                </Typography>
              </Box>
            ) : (
              <Box>
                {filteredItems.map((item) => (
                  <Box
                    key={item.id}
                    className={`item-row ${
                      selectedItems.has(item.id) ? "selected" : ""
                    }`}
                  >
                    <Checkbox
                      checked={selectedItems.has(item.id)}
                      onChange={() => handleToggleItem(item.id)}
                      className="item-checkbox"
                    />

                    {item.type === "folder" ? (
                      <Box
                        onClick={() => handleFolderClick(item)}
                        className="folder-item"
                      >
                        <FolderOpen className="folder-icon" />
                        <Box className="item-details">
                          <Typography variant="body1" className="item-name">
                            {item.name}
                          </Typography>
                          <Typography variant="body2" className="item-info">
                            {(item as any).fileCount} files
                          </Typography>
                        </Box>
                        <ArrowForward className="folder-arrow" />
                      </Box>
                    ) : (
                      <Box className="file-item">
                        <Description className="file-icon" />
                        <Box className="item-details">
                          <Typography variant="body1" className="item-name">
                            {item.name}
                          </Typography>
                          <Box className="file-metadata">
                            <Typography variant="body2" className="file-size">
                              {(item as CloudFile).size}
                            </Typography>
                            <Typography
                              variant="body2"
                              className="file-modified"
                            >
                              Modified {(item as CloudFile).modified}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {selectedItems.has(item.id) && (
                      <Check className="selected-icon" />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Divider />
          <Box className="browse-footer">
            <Box className="selection-info">
              <Typography variant="body1" className="selection-count">
                {selectedCount} items selected
              </Typography>
              <Typography variant="body2" className="files-count">
                {totalFilesSelected}{" "}
                {totalFilesSelected === 1 ? "file" : "files"} to import
              </Typography>
            </Box>

            <Button
              onClick={handleStartImport}
              disabled={
                selectedFiles.length === 0 || !classificationName.trim()
              }
              variant="contained"
              size="large"
              startIcon={<CloudUpload />}
              className="import-button"
            >
              Import & Classify
            </Button>
          </Box>
        </Card>
      )}
    </div>
  );
};

export default CloudCrawler;
